/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from 'baseui/button';
import { HeadingMedium, LabelMedium } from 'baseui/typography';
import { Block } from 'baseui/block';
import logger from '../utils/logger';
import { captureAppException } from '../sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to our logger (only in development)
    logger.error('Error caught by ErrorBoundary:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // SAM-226: forward render errors to Sentry (no-op when Sentry is disabled)
    // with the React component stack for readable traces.
    captureAppException(error, errorInfo.componentStack);

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Block
          padding="scale800"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          backgroundColor="#f8f9fa"
          borderRadius="8px"
          border="1px solid #e9ecef"
        >
          <HeadingMedium color="#dc3545" marginBottom="scale400">
            Something went wrong
          </HeadingMedium>
          
          <LabelMedium color="#6c757d" marginBottom="scale600" $style={{ textAlign: 'center' }}>
            We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
          </LabelMedium>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Block
              backgroundColor="#fff"
              padding="scale400"
              borderRadius="4px"
              border="1px solid #dee2e6"
              marginBottom="scale600"
              maxWidth="600px"
            >
              <LabelMedium color="#dc3545" marginBottom="scale200">
                Error Details (Development Only):
              </LabelMedium>
              <pre style={{ 
                fontSize: '12px', 
                color: '#6c757d', 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {this.state.error.message}
                {this.state.error.stack && `\n\nStack Trace:\n${this.state.error.stack}`}
              </pre>
            </Block>
          )}

          <Button
            onClick={this.handleRetry}
            kind="primary"
            size="large"
          >
            Try Again
          </Button>
        </Block>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
